import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { seo } from "@/utils/seo";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  FileText,
  HeartPulse,
  Lock,
  Mail,
  Scale,
  ShieldCheck,
  Truck,
  Video,
} from "lucide-react";
import { z } from "zod";

const privacySearchSchema = z.object({
  tab: z
    .enum([
      "distance-sales",
      "delivery-return",
      "kvkk",
      "videos",
      "privacy",
      "terms",
    ])
    .optional()
    .default("distance-sales"),
});

export const Route = createFileRoute("/_app/privacy")({
  validateSearch: privacySearchSchema,
  head: () => ({
    meta: seo({
      title:
        "Mesafeli Satış Sözleşmesi, Teslimat & İade, KVKK ve Gizlilik | AtalayTheCoach",
      description:
        "AtalayTheCoach Mesafeli Satış Sözleşmesi, Teslimat ve İade Şartları, Kişisel Verilerin Korunması (KVKK) Aydınlatma Metni, Antrenman Form Videoları Açık Rıza Şartları ve Gizlilik Politikası.",
      canonical: "https://atalaythecoach.com/privacy",
    }),
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  const handleTabChange = (newTab: string) => {
    navigate({
      to: "/privacy",
      search: {
        tab: newTab as
          | "distance-sales"
          | "delivery-return"
          | "kvkk"
          | "videos"
          | "privacy"
          | "terms",
      },
      replace: true,
    });
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      {/* Sade & Zarif Başlık Alanı */}
      <div className="border-border/40 border-b bg-muted/20 py-8">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <h1 className="font-bold text-2xl text-foreground tracking-tight sm:text-3xl">
            Sözleşmeler ve Yasal Bilgilendirmeler
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-muted-foreground text-xs leading-relaxed sm:text-sm">
            Tüketici hakları, mesafeli satış, teslimat & iade şartları, KVKK
            aydınlatma metni ve gizlilik politikası.
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 text-muted-foreground text-xs">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            <span>256-Bit SSL & İyzico Güvenli Ödeme Güvencesi</span>
          </div>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className="container mx-auto max-w-4xl px-4 pt-6">
        <Tabs
          value={tab}
          onValueChange={handleTabChange}
          className="w-full space-y-6"
        >
          {/* Sade Tab Listesi */}
          <div className="no-scrollbar overflow-x-auto pb-1">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-xl border border-border/60 bg-muted/30 p-1 sm:grid-cols-3 lg:grid-cols-6">
              <TabsTrigger
                value="distance-sales"
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 font-medium text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">Mesafeli Satış</span>
              </TabsTrigger>
              <TabsTrigger
                value="delivery-return"
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 font-medium text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
              >
                <Truck className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">Teslimat & İade</span>
              </TabsTrigger>
              <TabsTrigger
                value="kvkk"
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 font-medium text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
              >
                <Scale className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">KVKK</span>
              </TabsTrigger>
              <TabsTrigger
                value="videos"
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 font-medium text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
              >
                <Video className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">Video İzinleri</span>
              </TabsTrigger>
              <TabsTrigger
                value="privacy"
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 font-medium text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
              >
                <Lock className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">Gizlilik</span>
              </TabsTrigger>
              <TabsTrigger
                value="terms"
                className="flex items-center justify-center gap-1.5 rounded-lg py-2 font-medium text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
              >
                <HeartPulse className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">Kullanım & Sağlık</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: Mesafeli Satış Sözleşmesi */}
          <TabsContent
            value="distance-sales"
            className="space-y-4 outline-none"
          >
            <Card className="border-border/60 shadow-2xs">
              <CardHeader className="border-b bg-muted/10 pb-4">
                <div className="flex items-center gap-2.5">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-semibold text-lg">
                      Mesafeli Satış Sözleşmesi
                    </CardTitle>
                    <p className="mt-0.5 text-muted-foreground text-xs">
                      6502 Sayılı Kanun ve Mesafeli Sözleşmeler Yönetmeliği
                      Uyarınca
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-5 text-foreground/90 text-xs leading-relaxed sm:text-sm">
                {/* Taraflar */}
                <div className="grid gap-3 rounded-lg border border-border/70 bg-muted/20 p-3 text-xs sm:grid-cols-2">
                  <div>
                    <p className="font-semibold text-foreground">SATICI:</p>
                    <p className="mt-0.5 text-muted-foreground">
                      Alican Atalay (AtalayTheCoach)
                    </p>
                    <p className="mt-0.5 text-muted-foreground">
                      info@atalaythecoach.com • atalaythecoach.com
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      ALICI (Tüketici):
                    </p>
                    <p className="mt-0.5 text-muted-foreground">
                      Platform üzerinden online koçluk, danışmanlık veya mağaza
                      ürünlerini elektronik ortamda sipariş eden kişi.
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    1. Sözleşmenin Konusu
                  </h3>
                  <p className="text-muted-foreground">
                    İşbu sözleşmenin konusu, ALICI'nın SATICI'ya ait
                    atalaythecoach.com web sitesi üzerinden elektronik ortamda
                    siparişini yaptığı online koçluk hizmeti, antrenman/beslenme
                    danışmanlığı, dijital spor içerikleri veya spor ürünlerinin
                    satışı ve teslimi ile ilgili yasal hak ve yükümlülüklerin
                    belirlenmesidir.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    2. Ödeme ve Güvenlik
                  </h3>
                  <p className="text-muted-foreground">
                    Ödemeler, <strong>İyzico Güvenli Ödeme Altyapısı</strong>{" "}
                    üzerinden 256-bit SSL şifreleme ve 3D Secure ile kredi kartı
                    veya banka kartı ile tahsil edilir. Kart bilgileri
                    sunucularımızda saklanmaz.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    3. Teslimat ve İfa Şekli
                  </h3>
                  <p className="text-muted-foreground">
                    Dijital koçluk paketlerinde, ödemenin onaylanmasının
                    ardından sporcu bilgi formunun tamamlanmasını takiben en geç
                    24-48 saat içerisinde kişiselleştirilmiş program sisteme
                    yüklenir ve koçluk iletişimi başlar. Fiziksel ürünler 1-3 iş
                    günü içinde kargoya verilir.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    4. Cayma Hakkı ve İstisnaları
                  </h3>
                  <p className="text-muted-foreground">
                    ALICI, genel olarak 14 gün içinde cayma hakkına sahiptir.
                    Ancak Mesafeli Sözleşmeler Yönetmeliği Madde 15 uyarınca;{" "}
                    <strong>
                      tüketicinin istekleri veya kişisel ihtiyaçları
                      doğrultusunda özel olarak hazırlanan mallar/hizmetler
                      (kişiye özel antrenman programları)
                    </strong>{" "}
                    ve{" "}
                    <strong>
                      elektronik ortamda anında ifa edilen dijital içerikler
                    </strong>{" "}
                    için cayma hakkı kullanılamaz.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    5. İptal, İade ve Uyuşmazlıklar
                  </h3>
                  <p className="text-muted-foreground">
                    Şartlara uygun iptal veya iadelerde tutar, İyzico üzerinden
                    ALICI'nın kartına 7-14 iş günü içinde kesintisiz iade
                    edilir. Uyuşmazlıklarda ALICI'nın yerleşim yerindeki
                    Tüketici Hakem Heyetleri ve Tüketici Mahkemeleri yetkilidir.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 2: Teslimat ve İade Politikası */}
          <TabsContent
            value="delivery-return"
            className="space-y-4 outline-none"
          >
            <Card className="border-border/60 shadow-2xs">
              <CardHeader className="border-b bg-muted/10 pb-4">
                <div className="flex items-center gap-2.5">
                  <Truck className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-semibold text-lg">
                      Teslimat ve İade Politikası
                    </CardTitle>
                    <p className="mt-0.5 text-muted-foreground text-xs">
                      Hizmet ve Ürün Teslimat, İptal ve İade Süreçleri
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-5 text-foreground/90 text-xs leading-relaxed sm:text-sm">
                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    1. Hizmet ve Ürün Teslimatı
                  </h3>
                  <ul className="list-inside list-disc space-y-1 text-muted-foreground">
                    <li>
                      <strong>Online Koçluk:</strong> İyzico ile ödemenin
                      ardından hesap anında açılır. Sporcu bilgi formunun
                      iletilmesini takiben 24-48 saat içinde özel program
                      sisteme yüklenir.
                    </li>
                    <li>
                      <strong>Fiziksel Mağaza Ürünleri:</strong> Siparişler 1-3
                      iş günü içinde anlaşmalı kargoya verilir ve takip numarası
                      e-posta ile iletilir.
                    </li>
                  </ul>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    2. Sipariş İptali
                  </h3>
                  <p className="text-muted-foreground">
                    Henüz hazırlığına başlanmamış koçluk kayıtları veya
                    kargolanmamış ürünler için{" "}
                    <strong>info@atalaythecoach.com</strong> adresine e-posta
                    göndererek anında iptal talebinde bulunabilirsiniz.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    3. Geri Ödeme Süreci
                  </h3>
                  <p className="text-muted-foreground">
                    Onaylanan iadeler, İyzico güvenli ödeme altyapısı üzerinden
                    ödemenin yapıldığı kredi kartına veya banka kartına 7-14 iş
                    günü içinde kesintisiz olarak iade edilir.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    4. Hasarlı Kargo Bildirimi
                  </h3>
                  <p className="text-muted-foreground">
                    Fiziksel ürünlerde kargo teslimatı sırasında pakette hasar
                    görülmesi durumunda kargo görevlisine "Hasar Tespit
                    Tutanağı" tutturularak ürün teslim alınmamalıdır.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: KVKK Aydınlatma Metni */}
          <TabsContent value="kvkk" className="space-y-4 outline-none">
            <Card className="border-border/60 shadow-2xs">
              <CardHeader className="border-b bg-muted/10 pb-4">
                <div className="flex items-center gap-2.5">
                  <Scale className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-semibold text-lg">
                      KVKK Aydınlatma Metni
                    </CardTitle>
                    <p className="mt-0.5 text-muted-foreground text-xs">
                      6698 Sayılı Kişisel Verilerin Korunması Kanunu Kapsamında
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-5 text-foreground/90 text-xs leading-relaxed sm:text-sm">
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3 text-xs">
                  <p className="font-semibold text-foreground">
                    Veri Sorumlusu:{" "}
                    <span className="font-normal text-muted-foreground">
                      Alican Atalay (AtalayTheCoach) • info@atalaythecoach.com
                    </span>
                  </p>
                </div>

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    1. Veri İşleme Amaçları
                  </h3>
                  <p className="text-muted-foreground">
                    Kişisel verileriniz; kişiselleştirilmiş antrenman/güç
                    programlarının hazırlanması, egzersiz form analizi
                    yapılması, kullanıcı hesabı ve oturum güvenliği temini,
                    İyzico üzerinden yasal ödeme/fatura süreçlerinin yürütülmesi
                    amacıyla 6698 sayılı Kanun'un 5. ve 6. maddelerine uygun
                    olarak işlenmektedir.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    2. Veri Aktarımı
                  </h3>
                  <p className="text-muted-foreground">
                    Kişisel verileriniz yalnızca platformun güvenli çalışması
                    için güvenli bulut ve ödeme sağlayıcıları (İyzico, AWS,
                    Turso, Resend) ile paylaşılır. Verileriniz kesinlikle reklam
                    amacıyla üçüncü şahıslara satılmaz.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    3. KVKK Madde 11 Haklarınız
                  </h3>
                  <p className="text-muted-foreground">
                    Verilerinizin işlenip işlenmediğini öğrenme, düzeltilmesini,
                    silinmesini veya video kayıtlarınızın sistemden
                    kaldırılmasını talep etme hakkına sahipsiniz.
                    Başvurularınızı <strong>info@atalaythecoach.com</strong>{" "}
                    adresine iletebilirsiniz.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: Video Açık Rıza Metni */}
          <TabsContent value="videos" className="space-y-4 outline-none">
            <Card className="border-border/60 shadow-2xs">
              <CardHeader className="border-b bg-muted/10 pb-4">
                <div className="flex items-center gap-2.5">
                  <Video className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-semibold text-lg">
                      Antrenman Videoları ve Açık Rıza Beyanı
                    </CardTitle>
                    <p className="mt-0.5 text-muted-foreground text-xs">
                      Egzersiz Form Analizi ve Video Gizlilik Garantisi
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-5 text-foreground/90 text-xs leading-relaxed sm:text-sm">
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
                  <p className="font-semibold text-foreground">
                    Gizlilik Taahhüdü:
                  </p>
                  <p className="mt-0.5 text-muted-foreground">
                    Sisteme yüklediğiniz egzersiz videoları{" "}
                    <strong>YALNIZCA</strong> teknik form analizi ve antrenman
                    geri bildirimi için kullanılır. Açık yazılı izniniz olmadan
                    hiçbir sosyal medyada veya üçüncü kişilerle kesinlikle
                    paylaşılmaz.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    Depolama ve Silme Hakkı
                  </h3>
                  <p className="text-muted-foreground">
                    Videolar AWS S3 üzerinde şifrelenmiş olarak saklanır.
                    Dilediğiniz zaman kendi panelinizden silebilir veya tüm
                    arşivin silinmesini talep edebilirsiniz.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 5: Gizlilik Politikası */}
          <TabsContent value="privacy" className="space-y-4 outline-none">
            <Card className="border-border/60 shadow-2xs">
              <CardHeader className="border-b bg-muted/10 pb-4">
                <div className="flex items-center gap-2.5">
                  <Lock className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-semibold text-lg">
                      Gizlilik Politikası
                    </CardTitle>
                    <p className="mt-0.5 text-muted-foreground text-xs">
                      Çerezler, Ödeme Güvenliği ve Veri Koruma
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-5 text-foreground/90 text-xs leading-relaxed sm:text-sm">
                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    1. Çerezler ve Oturum Güvenliği
                  </h3>
                  <p className="text-muted-foreground">
                    Platformumuzda yalnızca güvenli oturum çerezleri (HTTP-Only)
                    ve tema tercihleri yerel hafızada saklanır. Üçüncü taraf
                    takip veya reklam çerezi bulunmaz.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    2. İyzico ile 256-Bit SSL Ödeme
                  </h3>
                  <p className="text-muted-foreground">
                    Ödemeler BDDK ve TCMB lisanslı İyzico altyapısı üzerinden
                    256-bit SSL şifreleme ve 3D Secure ile korunur. Kredi kartı
                    verileri tarafımızca işlenmez veya saklanmaz.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 6: Kullanım Koşulları & Sağlık Beyanı */}
          <TabsContent value="terms" className="space-y-4 outline-none">
            <Card className="border-border/60 shadow-2xs">
              <CardHeader className="border-b bg-muted/10 pb-4">
                <div className="flex items-center gap-2.5">
                  <HeartPulse className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="font-semibold text-lg">
                      Kullanım Koşulları ve Sağlık Beyanı
                    </CardTitle>
                    <p className="mt-0.5 text-muted-foreground text-xs">
                      Egzersiz Güvenliği ve Sağlık Sorumluluk Reddi
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-5 text-foreground/90 text-xs leading-relaxed sm:text-sm">
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
                  <p className="font-semibold text-amber-700 dark:text-amber-400">
                    Sağlık Uyarısı:
                  </p>
                  <p className="mt-0.5 text-muted-foreground">
                    Ağırlık antrenmanları yüksek fiziksel efor gerektirir.
                    Programa başlamadan önce hekim kontrolünden geçmeniz
                    önerilir. Platformdaki içerikler tıbbi tavsiye niteliği
                    taşımaz.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <h3 className="font-semibold text-foreground">
                    Fikri Mülkiyet
                  </h3>
                  <p className="text-muted-foreground">
                    Platformda yer alan tüm antrenman programları, algoritmalar
                    ve içeriklerin telif hakları AtalayTheCoach'a aittir;
                    izinsiz kopyalanamaz veya paylaşılamaz.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Minimal İletişim Kutusu */}
        <div className="mt-8 flex flex-col items-center justify-between gap-3 rounded-xl border border-border/60 bg-muted/20 px-5 py-4 text-center sm:flex-row sm:text-left">
          <div className="text-xs">
            <span className="font-semibold text-foreground">
              Sorularınız veya Sözleşme Talepleriniz için:
            </span>{" "}
            <span className="text-muted-foreground">
              info@atalaythecoach.com
            </span>
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-8 rounded-lg text-xs"
          >
            <a href="mailto:info@atalaythecoach.com">
              <Mail className="mr-1.5 h-3.5 w-3.5 text-primary" />
              E-posta Gönder
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
